import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';
import { useAuth } from '../App';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import procoreLogo from '../assets/procore-logo.png';
import quickbooksLogo from '../assets/quickbooks-logo.png';

export default function Dashboard() {
  const { connections, checkConnections } = useAuth();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncingType, setSyncingType] = useState(null);
  const [showClearConfirm, setShowClearConfirm] = useState(false);
  const [schedule, setSchedule] = useState('hourly');
  const [showSchedule, setShowSchedule] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    if (searchParams.get('procore') === 'connected' || searchParams.get('quickbooks') === 'connected') {
      checkConnections();
    }
    loadSummary();
    axios.get('/api/dashboard/schedule').then(r => setSchedule(r.data.schedule)).catch(() => {});
  }, []);

  const updateSchedule = async (val) => {
    await axios.post('/api/dashboard/schedule', { schedule: val });
    setSchedule(val);
    setShowSchedule(false);
  };

  const SCHEDULE_LABELS = {
    off: '⏸ Off',
    hourly: '↻ Every Hour',
    '6hours': '↻ Every 6 Hours',
    '12hours': '↻ Every 12 Hours',
    daily: '↻ Every Day',
  };

  const loadSummary = async () => {
    try {
      const res = await axios.get('/api/dashboard/summary');
      setSummary(res.data);
    } catch (err) {
    } finally {
      setLoading(false);
    }
  };

  const connectProcore = () => {
    const token = localStorage.getItem('token');
    const payload = JSON.parse(atob(token.split('.')[1]));
    window.location.href = `${process.env.REACT_APP_API_URL}/auth/procore?tenantId=${payload.tenantId}`;
  };

  const connectQuickBooks = () => {
    const token = localStorage.getItem('token');
    const payload = JSON.parse(atob(token.split('.')[1]));
    window.location.href = `${process.env.REACT_APP_API_URL}/auth/quickbooks?tenantId=${payload.tenantId}`;
  };

  const clearActivity = async () => {
    setShowClearConfirm(false);
    try {
      await axios.delete('/api/dashboard/clear-activity');
      loadSummary();
    } catch (err) {
      alert('Failed to clear: ' + err.message);
    }
  };

  const triggerSync = async (syncType) => {
    setSyncingType(syncType);
    try {
      await axios.post('/api/sync/trigger', { syncType });
      setTimeout(() => { loadSummary(); setSyncingType(null); }, 2000);
    } catch (err) {
      alert('Sync failed: ' + (err.response?.data?.error || err.message));
      setSyncingType(null);
    }
  };

  const isFullyConnected = connections.procore && connections.quickbooks;
  const syncChartData = summary?.syncStats ? buildChartData(summary.syncStats) : [];

  return (
    <div className="app-layout" style={{ display: 'flex' }}>
      <Sidebar pendingApprovals={summary?.pendingApprovals || 0} />
      <main className="app-main" style={{ flex: 1, minWidth: 0 }}>
        {/* Page header */}
        <div className="page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
          <div>
            <h2 className="page-title">Dashboard</h2>
            <p className="page-sub">Real-time sync status between Procore and QuickBooks</p>
          </div>
          <div style={{ position: 'relative' }}>
            <div
              onClick={() => setShowSchedule(!showSchedule)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8,
                color: schedule === 'off' ? 'var(--muted2)' : 'var(--success)',
                fontSize: 13, cursor: 'pointer',
                padding: '6px 10px', borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.1)',
                background: 'rgba(255,255,255,0.03)',
              }}
            >
              <div style={{
                width: 8, height: 8, borderRadius: '50%',
                background: schedule === 'off' ? 'var(--muted2)' : 'var(--success)',
                animation: schedule === 'off' ? 'none' : 'pulse 2s ease-in-out infinite'
              }} />
              {SCHEDULE_LABELS[schedule] || '↻ Every Hour'} ▾
            </div>
            {showSchedule && (
              <div style={{
                position: 'absolute', top: '110%', right: 0, zIndex: 100,
                background: '#0D1117', border: '1px solid rgba(255,255,255,0.12)',
                borderRadius: 10, overflow: 'hidden', minWidth: 160,
              }}>
                {Object.entries(SCHEDULE_LABELS).map(([val, label]) => (
                  <div
                    key={val}
                    onClick={() => updateSchedule(val)}
                    style={{
                      padding: '10px 14px', fontSize: 13, cursor: 'pointer',
                      color: schedule === val ? 'var(--accent)' : 'var(--text)',
                      background: schedule === val ? 'rgba(110,231,255,0.08)' : 'transparent',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.05)'}
                    onMouseLeave={e => e.currentTarget.style.background = schedule === val ? 'rgba(110,231,255,0.08)' : 'transparent'}
                  >
                    {label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Connection Banner */}
        {!isFullyConnected && (
          <div className="alert alert-warning" style={{ marginBottom: 24 }}>
            ⚠ Connect both platforms below to start syncing automatically.
          </div>
        )}

        {/* Connect Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 16, marginBottom: 24 }}>
          <ConnectCard
            name="Procore"
            connected={connections.procore}
            onConnect={connectProcore}
            description="Project management & financials"
          />
          <ConnectCard
            name="QuickBooks"
            connected={connections.quickbooks}
            onConnect={connectQuickBooks}
            description="Accounting & job costing"
          />
        </div>

        {/* Stats Row */}
        {summary && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
            <div className="stat-card">
              <div className="stat-label">Vendors Synced</div>
              <div className="stat-value">{summary.mappingStats?.vendors_synced || 0}</div>
              <div className="stat-label" style={{ marginTop: 4 }}>Procore ↔ QB matched</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Projects Mapped</div>
              <div className="stat-value">{summary.mappingStats?.projects_synced || 0}</div>
              <div className="stat-label" style={{ marginTop: 4 }}>Jobs linked to customers</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Cost Codes Mapped</div>
              <div className="stat-value">{summary.mappingStats?.cost_codes_mapped || 0}</div>
              <div className="stat-label" style={{ marginTop: 4 }}>AI-mapped to QB items</div>
            </div>
            <div className="stat-card">
              <div className="stat-label">Pending Approvals</div>
              <div className="stat-value">{summary.pendingApprovals}</div>
              <div className="stat-label" style={{ marginTop: 4 }}>
                {summary.pendingApprovals > 0
                  ? <span style={{ cursor: 'pointer', color: 'var(--warning)' }} onClick={() => navigate('/gatekeeper')}>Review now →</span>
                  : 'All clear'}
              </div>
            </div>
          </div>
        )}

        {/* Sync Controls + Recent Activity */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 20 }}>
          {/* Sync Controls */}
          <div className="card card-glass">
            <div className="card-header">
              <span className="card-title">Sync Controls</span>
              {!isFullyConnected && <span className="badge">Connect both platforms first</span>}
            </div>
            <div className="card-body" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {SYNC_TYPES.map(s => (
                <SyncRow
                  key={s.type}
                  {...s}
                  disabled={!isFullyConnected || syncingType !== null}
                  loading={syncingType === s.type}
                  onSync={() => triggerSync(s.type)}
                />
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="card card-glass">
            <div className="card-header">
              <span className="card-title">Recent Activity</span>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-danger btn-sm" onClick={() => setShowClearConfirm(true)}>↺ Clear</button>
                <button className="btn btn-ghost btn-sm" onClick={loadSummary}>↻ Refresh</button>
                <button className="btn btn-ghost btn-sm" onClick={() => navigate('/logs')}>View all</button>
              </div>
            </div>
            <div className="card-body">
              {loading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
                  <div className="spinner" />
                </div>
              ) : summary?.recentLogs?.length === 0 ? (
                <div className="empty-state">
                  <div className="empty-state-icon">📋</div>
                  <div className="empty-state-title">No sync activity yet</div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {(summary?.recentLogs || []).slice(0, 8).map(log => (
                    <div key={log.id} style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '9px 0',
                      borderBottom: '1px solid rgba(255,255,255,0.05)',
                    }}>
                      <StatusDot status={log.status} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600, textTransform: 'capitalize', color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                          {log.sync_type.replace(/_/g, ' ')} sync
                        </div>
                        <div style={{ fontSize: 11.5, color: 'var(--muted2)' }}>
                          {log.records_processed} records · {formatTime(log.started_at)}
                        </div>
                      </div>
                      <span className={`badge badge-${statusColor(log.status)}`}>{log.status}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Sync Stats Chart */}
        {syncChartData.length > 0 && (
          <div className="card card-glass" style={{ marginTop: 20 }}>
            <div className="card-header">
              <span className="card-title">Sync Activity — Last 30 Days</span>
            </div>
            <div className="card-body">
              <ResponsiveContainer width="100%" height={180}>
                <BarChart data={syncChartData} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
                  <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: 'rgba(255,255,255,0.4)', fontSize: 11 }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: '#0D1117', border: '1px solid rgba(255,255,255,0.12)', borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: 'var(--text)' }}
                  />
                  <Bar dataKey="success" fill="var(--success)" radius={[3,3,0,0]} opacity={0.85} />
                  <Bar dataKey="error" fill="var(--danger)" radius={[3,3,0,0]} opacity={0.85} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}

        {/* Clear Activity Modal */}
        {showClearConfirm && (
          <div className="modal-overlay">
            <div className="modal">
              <h3 className="modal-title">Clear Recent Activity?</h3>
              <p className="modal-desc">
                This will permanently delete all sync logs and gatekeeper items. This cannot be undone.
              </p>
              <div className="modal-actions">
                <button className="btn btn-ghost" onClick={() => setShowClearConfirm(false)}>Cancel</button>
                <button className="btn btn-danger" onClick={clearActivity}>Clear Activity</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}

function ConnectCard({ name, connected, onConnect, description }) {
  const logo = name === 'Procore' ? procoreLogo : quickbooksLogo;
  return (
    <div className="card card-glass" style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
      <div style={{
        width: 44, height: 44, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <img src={logo} alt={name} style={{ width: 44, height: 44, objectFit: 'contain' }} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--text)' }}>{name}</div>
        <div style={{ color: 'var(--muted2)', fontSize: 12 }}>{description}</div>
      </div>
      <span className={`connection-pill ${connected ? 'connected' : 'disconnected'}`}>
        <span className={`status-dot ${connected ? 'connected' : 'disconnected'}`} />
        {connected ? 'Connected' : <button className="btn btn-primary btn-sm" onClick={onConnect}>Connect</button>}
      </span>
    </div>
  );
}

function SyncRow({ type, label, description, disabled, loading, onSync }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12,
      padding: '10px 12px',
      background: 'rgba(255,255,255,0.03)',
      borderRadius: 12,
      border: '1px solid rgba(255,255,255,0.08)',
    }}>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{label}</div>
        <div style={{ fontSize: 11.5, color: 'var(--muted2)', marginTop: 1 }}>{description}</div>
      </div>
      <button className="btn btn-ghost btn-sm" disabled={disabled} onClick={onSync}>
        {loading ? <><div className="spinner" style={{ width: 12, height: 12 }} /> Syncing</> : '▶ Run'}
      </button>
    </div>
  );
}

function StatusDot({ status }) {
  const map = { success: 'var(--success)', error: 'var(--danger)', pending: 'var(--warning)' };
  const color = map[status] || 'var(--muted2)';
  return <div style={{ width: 7, height: 7, borderRadius: '50%', background: color, flexShrink: 0 }} />;
}

function statusColor(status) {
  return { success: 'success', error: 'danger', pending: 'warning', skipped: 'muted' }[status] || 'muted';
}

function formatTime(ts) {
  const d = new Date(ts);
  const now = new Date();
  const diff = Math.floor((now - d) / 1000);
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
}

function buildChartData(syncStats) {
  const map = {};
  syncStats.forEach(s => {
    if (!map[s.sync_type]) map[s.sync_type] = { name: s.sync_type.replace(/_/g, ' '), success: 0, error: 0 };
    map[s.sync_type][s.status] = parseInt(s.count);
  });
  return Object.values(map);
}

const SYNC_TYPES = [
  { type: 'vendor', label: 'Vendor Sync', description: 'Sync vendor list between Procore and QuickBooks' },
  { type: 'project', label: 'Project / Job Sync', description: 'Map Procore projects to QB customers/jobs' },
{ type: 'budget', label: 'Budget Export', description: 'Export locked Procore budgets as QB estimates' },
  { type: 'payment_status', label: 'Payment Status', description: 'Pull QB payment status back into Procore' },
  { type: 'commitment', label: 'Sync Commitments / POs', description: 'Push purchase orders and subcontracts to QB' },
  { type: 'subcontract_invoice', label: 'Sync Subcontract Invoices → Bills', description: 'Push subcontractor invoices to QB as bills' },
  { type: 'prime_contract_invoice', label: 'Sync Prime Contract Invoices', description: 'Push prime contract invoices to QB as sales invoices' },
  { type: 'expense', label: 'Sync Expenses → Bills', description: 'Push direct cost expenses to QB as bills' },
  { type: 'sub_job', label: 'Sync Sub Jobs', description: 'Push Procore sub jobs to QB as sub-customers' },
  { type: 'tax_code', label: 'Sync Tax Codes', description: 'Map Procore tax codes to QB tax rates for review' },
  { type: 'employee', label: 'Sync Employees', description: 'Sync Procore workers to QB employees bidirectionally' },
  { type: 'timesheet', label: 'Sync Timesheets', description: 'Push Procore timesheet entries to QB time activities' },
];
